/**
 * The setup instructions a customer hands to their own engineer or coding
 * agent to add Cofactor as a sign-in provider in their app.
 *
 * Every security requirement lives in this text, so it is built by one pure
 * function with a test that pins each requirement: a paraphrase that drops a
 * guardrail is a security regression no type checker would catch. The text
 * says plainly which controls are the customer's call; it never claims a
 * control Cofactor does not have.
 *
 * @see docs/features/credentials.md (Test sign-in)
 */

export type TestSignInStack =
  | 'generic'
  | 'authjs'
  | 'passport'
  | 'allauth'
  | 'auth0'
  | 'cognito'
  | 'okta'
  | 'entra'
  | 'keycloak'
  | 'clerk'
  | 'firebase';

export const TEST_SIGN_IN_STACKS: ReadonlyArray<{ id: TestSignInStack; label: string }> = [
  { id: 'generic', label: 'Any OpenID Connect library' },
  { id: 'authjs', label: 'Auth.js / NextAuth' },
  { id: 'passport', label: 'Passport (passport-openidconnect)' },
  { id: 'allauth', label: 'django-allauth' },
  { id: 'auth0', label: 'Auth0' },
  { id: 'cognito', label: 'Amazon Cognito' },
  { id: 'okta', label: 'Okta' },
  { id: 'entra', label: 'Microsoft Entra ID' },
  { id: 'keycloak', label: 'Keycloak' },
  { id: 'clerk', label: 'Clerk' },
  { id: 'firebase', label: 'Firebase Authentication' },
];

export type TestSignInSetupPromptInput = {
  stack: TestSignInStack;
  appName: string;
  issuer: string;
  clientId: string;
  /** Exact callback URLs registered for this client. */
  redirectUris: string[];
  emailDomain: string;
};

function stackSteps(stack: TestSignInStack): string[] {
  switch (stack) {
    case 'authjs':
      return [
        'Add a provider entry with `type: "oidc"`, `id: "cofactor"`, `name: "Cofactor"`, the issuer, client ID and client secret below, and `checks: ["pkce", "state", "nonce"]`.',
        'Auth.js starts a sign-in from a POST, so a test run clicks your "Sign in with Cofactor" button. Show that button on staging only; in Cofactor, set the credential\'s sign-in button selector to match it.',
      ];
    case 'passport':
      return [
        'Register a `passport-openidconnect` strategy named `cofactor` with the issuer, the endpoints from the discovery document, the client ID and secret, and scope `openid email profile`.',
        'Mount `GET /auth/cofactor` → `passport.authenticate("cofactor")` and its callback route, on staging only. In Cofactor, set the credential\'s start URL to that route.',
      ];
    case 'allauth':
      return [
        'Add an `openid_connect` provider app with `provider_id: "cofactor"`, the client ID and secret, and `settings.server_url` set to the issuer.',
        'Link to the provider\'s login URL (`/accounts/oidc/cofactor/login/`) on staging only. In Cofactor, set the credential\'s start URL to it, or its sign-in button selector if login needs a POST.',
      ];
    case 'generic':
      return [
        'Register Cofactor as an OpenID Connect provider using the discovery document at `{issuer}/.well-known/openid-configuration`, the authorization code flow, and PKCE (S256).',
        'Expose a route that starts that sign-in, on staging only. In Cofactor, set the credential\'s start URL to it, or its sign-in button selector if the sign-in starts from a button.',
      ];
    default:
      return [
        'In the identity service\'s admin console, add a custom OpenID Connect identity provider (sometimes called an enterprise or social connection) using the issuer, client ID and client secret below. Some plans gate custom OIDC providers; check yours first.',
        'Make the connection available to the staging application only, and add a staging-only way to start it (a button or a link). In Cofactor, set the credential\'s start URL or sign-in button selector to match.',
      ];
  }
}

/**
 * Values interpolated into code spans come from admin input. A backtick would
 * end the span and reformat the instructions, so URLs carry it percent-encoded
 * (the same URL) and names drop it.
 */
const urlInCode = (value: string) => value.replaceAll('`', '%60');
const nameInText = (value: string) => value.replaceAll('`', '');

/** Markdown instructions for adding Cofactor Test sign-in to one app. */
export function buildTestSignInSetupPrompt(input: TestSignInSetupPromptInput): string {
  const stackLabel = TEST_SIGN_IN_STACKS.find((s) => s.id === input.stack)?.label ?? input.stack;
  const callbacks = input.redirectUris.map((uri) => `  - \`${urlInCode(uri)}\``).join('\n');

  return `# Add "Sign in with Cofactor" to ${nameInText(input.appName)} (staging)

You are adding Cofactor as an extra OpenID Connect sign-in provider so Cofactor's automated tests can sign in as dedicated test users. Real users keep signing in exactly as they do today. The app uses ${stackLabel}.

## Values

- Issuer: \`${input.issuer}\`
- Discovery document: \`${input.issuer}/.well-known/openid-configuration\`
- Client ID: \`${input.clientId}\`
- Client secret: shown once in Cofactor when the client was created. Put it in your secret store or environment (for example \`COFACTOR_CLIENT_SECRET\`). Never commit it.
- Registered callback URLs (the app must send exactly one of these as \`redirect_uri\`):
${callbacks}
- Scopes: \`openid email profile\`
- Token endpoint authentication: \`client_secret_basic\` (or \`client_secret_post\`)
- Test user email domain: \`@${input.emailDomain}\`

## Steps

${stackSteps(input.stack)
  .map((step, i) => `${i + 1}. ${step.replace('{issuer}', input.issuer)}`)
  .join('\n')}
3. Accept an identity from this provider only when \`email_verified\` is true and the email ends with \`@${input.emailDomain}\`. Reject every other address, even if the provider sends it.
4. Sign in existing users only: look the user up by email and refuse the sign-in if no account exists. Create the test users yourself, in the \`${input.emailDomain}\` domain, with the roles your tests need.
5. Enable all of this on staging and preview environments only. Do not configure it on production unless your team has decided to test production this way.

## Why these rules

- **Email domain.** Cofactor only vouches for addresses in \`${input.emailDomain}\`, and checks that on every sign-in. Checking it in your app as well means neither side alone can let an identity outside it in.
- **Existing users only.** Many sign-in libraries create an account the first time an identity appears. If you allow that here, an identity from Cofactor becomes a new account in your app. Looking users up instead means the only accounts reachable this way are the test users you created, and deleting them shuts the door. This is your decision; Cofactor cannot enforce it.
- **Staging only.** Cofactor cannot tell your staging and production apps apart. Keeping this configuration out of production is the control.
- **No human can sign in through Cofactor.** The provider only completes a sign-in that Cofactor started for a test run; anyone else who reaches it is sent back to your app with \`access_denied\`.

## Check it works

In Cofactor, open the Test sign-in credential for a test user and run **Save & Test**. If something is misconfigured, Cofactor shows the step that failed and the values involved, for example the \`redirect_uri\` your app sent next to the ones registered.
`;
}
